import { FormProvider, SubmitHandler } from 'react-hook-form';
import { NavLink, useNavigate } from 'react-router-dom';

import { Breadcrumb, Card, FormControl, LoadingButton, PageTitle, PageWrapper } from '@ingrid/luterkort-react';

import { FormLeaveBlocker } from '~src/components/FormLeaveBlocker/FormLeaveBlocker';
import { MutationBanner } from '~src/components/MutationNotification/MutationNotification';
import { useTranslation } from '~src/i18n/use-translation';
import { useToast } from '~src/main/hooks/useToast';
import { Addresses } from '~src/pages/shipment/create/addresses';
import { AddressesProvider } from '~src/pages/shipment/create/addresses-context';

import { useShipmentTemplateCreateMutation } from '../mutations';
import { ShipmentTemplateDetails, TemplateUpsert, useTemplateForm } from '../types';
import { BasicInformation } from './basic-information';
import { CarrierProduct } from './carrier-product';
import { Metadata } from './metadata';

interface Props {
  template?: ShipmentTemplateDetails;
}

export function Page({ template }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const methods = useTemplateForm({ defaultValues: template });
  const mutation = useShipmentTemplateCreateMutation();

  const onSubmit: SubmitHandler<TemplateUpsert> = async values => {
    await mutation.mutateAsync(values);

    toast({
      variant: 'success',
      title: t('shipmentTemplates.details.createSuccess'),
    });

    navigate('..');
  };

  return (
    <PageWrapper gap="lg" variant="center">
      <FormProvider {...methods}>
        <FormControl.Root>
          <FormControl.Form onSubmit={methods.handleSubmit(onSubmit)} style={{ display: 'none' }} />

          <PageTitle.Wrapper>
            <PageTitle.Stack>
              <Breadcrumb aria-label={t('common.breadcrumbs')}>
                <NavLink to="..">{t('common.templates')}</NavLink>
                <span aria-current="page">{t('common.addTemplate')}</span>
              </Breadcrumb>
            </PageTitle.Stack>

            <FormControl.SubmitButton asChild>
              <LoadingButton loading={mutation.isPending}>{t('common.create')}</LoadingButton>
            </FormControl.SubmitButton>
          </PageTitle.Wrapper>

          <MutationBanner mutation={mutation} />

          <AddressesProvider prefix="template">
            <Card.List>
              <BasicInformation />
              <Addresses />
              <CarrierProduct />
              <Metadata />
            </Card.List>
          </AddressesProvider>

          <FormLeaveBlocker formState={methods.formState} />
        </FormControl.Root>
      </FormProvider>
    </PageWrapper>
  );
}
